import React, { useState } from 'react';
import { Heart, BookOpen, Languages, Mail, FileEdit } from 'lucide-react';

export default function ContributePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contributionType: 'correction',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#064635] mb-4">
            Help Preserve This Scholarly Archive
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join us in making E.S.V. Tomori's research accessible to all. Whether through corrections,
            translations, research contributions, or financial support, your involvement helps ensure
            these important works remain available for future generations.
          </p>
        </div>

        {/* Contribution Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <FileEdit className="h-8 w-8 text-[#D4AF37] mb-4" />
            <h3 className="text-lg font-semibold text-[#064635] mb-2">Submit Corrections</h3>
            <p className="text-gray-600 text-sm">
              Help improve accuracy by submitting corrections or clarifications to existing publications.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <BookOpen className="h-8 w-8 text-[#D4AF37] mb-4" />
            <h3 className="text-lg font-semibold text-[#064635] mb-2">Share Research</h3>
            <p className="text-gray-600 text-sm">
              Contribute related research materials, references, or scholarly works that complement the archive.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <Heart className="h-8 w-8 text-[#D4AF37] mb-4" />
            <h3 className="text-lg font-semibold text-[#064635] mb-2">Support Digitization</h3>
            <p className="text-gray-600 text-sm">
              Donate to fund ongoing digitization efforts and help preserve more publications.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <Languages className="h-8 w-8 text-[#D4AF37] mb-4" />
            <h3 className="text-lg font-semibold text-[#064635] mb-2">Translate to Yoruba</h3>
            <p className="text-gray-600 text-sm">
              Help translate publications and interface elements into Yoruba language for wider accessibility.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-[#064635] mb-6">Get Involved</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="contributionType" className="block text-sm font-medium text-gray-700 mb-1">
                Type of Contribution
              </label>
              <select
                id="contributionType"
                name="contributionType"
                value={formData.contributionType}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
              >
                <option value="correction">Submit a Correction</option>
                <option value="research">Share Related Research</option>
                <option value="financial">Support Digitization (Donation)</option>
                <option value="translation">Help with Yoruba Translation</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                required
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full bg-[#064635] text-white px-6 py-3 rounded-lg font-semibold 
                       hover:bg-opacity-90 transition-colors duration-200 flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Submit
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}